import React from "react";
import { prettifyFileExtension } from "tools/file/prettifyFileExtension";
import { storage } from "api/firebase";
import { FIVE_MB_IN_BYTES } from "tools/file/bytesToMegaBytes";
import formatBytes from "tools/formats/formatBytes";
import { useSnackbar } from "notistack";
import { UseTranslateResult } from "@tolgee/react";
import { buttonStyles, TOAST_BUTTON_TEXT } from "./helpers";

type TranslateFn = UseTranslateResult["t"];

const storageRef = storage.ref();

const createLogoUploadListener = (
  path: string,
  onUploaded: (logo: string) => void,
  sizeLimit?: number,
  acceptFileTypesFromArgs?: string[]
) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  return async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    const limit = sizeLimit || FIVE_MB_IN_BYTES;

    if (!file) {
      return false;
    }

    const moreThenSizeLimit = file.size > limit;
    if (moreThenSizeLimit) {
      const message = getWrongSizeError(limit);

      enqueueSnackbar(message, {
        variant: "default",
        action: (key) =>
          React.createElement(
            "button",
            {
              onClick: () => closeSnackbar(key),
              type: "button",
              style: buttonStyles,
            },
            TOAST_BUTTON_TEXT
          ),
      });
      return false;
    }

    const acceptFileTypes =
      getFileTypeFacades(acceptFileTypesFromArgs) || logoAcceptTypes;
    if (!acceptFileTypes.includes(file.type)) {
      const message = FILE_FORMAT_ERROR(
        acceptFileTypesFromArgs || ["png", "jpg", "jpeg", "gif"]
      );
      enqueueSnackbar(message, {
        variant: "default",
        action: (key) =>
          React.createElement(
            "button",
            {
              onClick: () => closeSnackbar(key),
              type: "button",
              style: buttonStyles,
            },
            TOAST_BUTTON_TEXT
          ),
      });
      return false;
    }

    const ext = prettifyFileExtension(file);
    const fileRef = storageRef.child(`${path}.${ext}`);

    await fileRef.put(file);
    const logo: string = await fileRef.getDownloadURL();

    onUploaded(logo);
  };
};

const getFileTypeFacades = (fileTypes?: string[]) => {
  if (!fileTypes) {
    return undefined;
  }
  return fileTypes.map((fileType) => `image/${fileType}`);
};

export const logoAcceptTypes = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/gif",
];

export const getWrongSizeError = (limit: number) =>
  `File size exceeds the ${formatBytes(limit, -1)} limit`;

// `t` is optional so the logo-upload / organiser callers keep the English
// default. Pass it from a localized (task / grid) caller and the joiner is
// translated too — the joined result is interpolated into already-localized
// sentences, so an English " or " would leak into a translated string.
export const formatAcceptedExtensions = (
  acceptedExtensions: string[],
  t?: TranslateFn
): string => {
  const extensions = acceptedExtensions.map((ext) => ext.toUpperCase());

  if (!extensions.length) {
    return "";
  }

  if (extensions.length === 1) {
    return extensions[0];
  }

  const last = extensions[extensions.length - 1];
  const items = extensions
    .slice(0, -1)
    .join(t ? t("common.list.separator") : ", ");

  return t ? t("common.list.or_last", { items, last }) : `${items} or ${last}`;
};

export const FILE_FORMAT_ERROR = (
  acceptedExtensions: string[],
  prefix?: string,
  t?: TranslateFn
) => {
  const defaultPrefix = "Unsupported file type. Please upload a ";
  const defaultEnd = " file";
  const formattedExtensions = formatAcceptedExtensions(acceptedExtensions, t);

  // Localized path (no custom prefix): the whole sentence is one key.
  if (t && !prefix) {
    return t("common.tasks.file_upload.unsupported_type", {
      extensions: formattedExtensions,
    });
  }

  return `${prefix || defaultPrefix}${formattedExtensions}${defaultEnd}`;
};

export default createLogoUploadListener;
